import React, { useEffect, useMemo, useRef } from 'react'
import Map from 'ol/Map'
import View from 'ol/View'
import Feature from 'ol/Feature'
import { Point } from 'ol/geom'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import XYZ from 'ol/source/XYZ'
import VectorSource from 'ol/source/Vector'
import { defaults as defaultControls } from 'ol/control'
import { fromLonLat } from 'ol/proj'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'
import 'ol/ol.css'
import { useRecycling } from '../contexts/RecyclingContext'

// Minimal coordinate mapping for selected Hong Kong districts (approximate centers)
const districtCoords: Record<string, [number, number]> = {
  'Sha Tin': [22.387, 114.195],
  'Kowloon City': [22.328, 114.191],
  'Central & Western': [22.281, 114.158],
  'Eastern': [22.284, 114.223],
  'Southern': [22.247, 114.160],
  'Wan Chai': [22.276, 114.175],
  'Yuen Long': [22.445, 114.025],
  'Tuen Mun': [22.393, 113.974],
  'Tsuen Wan': [22.373, 114.118],
  'Kwun Tong': [22.312, 114.226],
  'Sai Kung': [22.382, 114.273]
}

interface HKDistrictMapProps {
  height?: string
}

const HKDistrictMap: React.FC<HKDistrictMapProps> = ({ height = '400px' }) => {
  const mapRef = useRef<HTMLDivElement | null>(null)
  const olMapRef = useRef<Map | null>(null)
  const vectorSourceRef = useRef<VectorSource | null>(null)

  const { getDistrictTotals } = useRecycling()
  const districts = getDistrictTotals()
  const maxPlastic = Math.max(1, ...districts.map(d => d.plasticKg))

  const mapPoints = useMemo(
    () =>
      districts
        .map((d) => {
          const coords = districtCoords[d.district]
          if (!coords) return null

          const radius = 8 + (d.plasticKg / maxPlastic) * 22
          const intensity = Math.min(1, d.plasticKg / maxPlastic)
          const color = intensity > 0.66 ? '#dc2626' : intensity > 0.33 ? '#f59e0b' : '#0d9488'
          const fill = intensity > 0.66 ? 'rgba(220,38,38,0.35)' : intensity > 0.33 ? 'rgba(245,158,11,0.35)' : 'rgba(13,148,136,0.35)'

          return {
            district: d.district,
            coords,
            radius,
            color,
            fill
          }
        })
        .filter((point): point is { district: string; coords: [number, number]; radius: number; color: string; fill: string } => Boolean(point)),
    [districts, maxPlastic]
  )

  useEffect(() => {
    if (!mapRef.current || olMapRef.current) return

    const baseLayer = new TileLayer({
      source: new XYZ({
        url: 'https://mapapi.geodata.gov.hk/gs/api/v1.0.0/xyz/basemap/wgs84/{z}/{x}/{y}.png',
        minZoom: 10,
        maxZoom: 20,
        crossOrigin: 'anonymous'
      })
    })

    const vectorSource = new VectorSource()
    const vectorLayer = new VectorLayer({ source: vectorSource })
    vectorSourceRef.current = vectorSource

    const map = new Map({
      target: mapRef.current,
      controls: defaultControls({ attribution: false }),
      layers: [baseLayer, vectorLayer],
      view: new View({
        center: fromLonLat([114.17, 22.33]),
        zoom: 11,
        minZoom: 10,
        maxZoom: 20
      })
    })

    olMapRef.current = map

    return () => {
      map.setTarget(undefined)
      olMapRef.current = null
      vectorSourceRef.current = null
    }
  }, [])

  useEffect(() => {
    const source = vectorSourceRef.current
    if (!source) return

    source.clear()

    mapPoints.forEach((point) => {
      const feature = new Feature({ geometry: new Point(fromLonLat([point.coords[1], point.coords[0]])) })
      feature.setStyle(
        new Style({
          image: new CircleStyle({
            radius: point.radius,
            stroke: new Stroke({ color: point.color, width: 2 }),
            fill: new Fill({ color: point.fill })
          })
        })
      )
      source.addFeature(feature)
    })
  }, [mapPoints])

  return (
    <div className="w-full">
      <div className="relative" style={{ height, width: '100%', borderRadius: '0.75rem', overflow: 'hidden' }}>
        <div ref={mapRef} className="h-full w-full" />
        <a
          href="https://api.portal.hkmapservice.gov.hk/disclaimer"
          target="_blank"
          rel="noreferrer"
          className="absolute bottom-2 right-12 z-[400] rounded bg-white/80 px-2 py-1 text-[11px] text-gray-800"
        >
          © Map information from Lands Department
        </a>
        <img
          src="https://api.hkmapservice.gov.hk/mapapi/landsdlogo.jpg"
          alt="LandsD logo"
          className="absolute bottom-2 right-2 z-[400] h-7 w-7 rounded bg-white/80 p-0.5"
        />
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {districts.map((d) => (
          <div key={d.district} className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700">
            <p className="font-semibold text-gray-900">{d.district}</p>
            <p>Plastic: {d.plasticKg} kg | Credits: {d.credits} PC</p>
            <p>Cans: {d.aluminiumKg} kg | Paper: {d.paperKg} kg | Glass: {d.glassKg} kg</p>
          </div>
        ))}
      </div>

      {districts.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500">No district data yet</div>
      )}
    </div>
  )
}

export default HKDistrictMap
